import React, { forwardRef } from 'react';
import { IconAdd, IconChevron } from '@components/icons';
import { LoaderSpinner } from '@components/loader-spinner';
import classNames from 'classnames';

import { getButtonCoreStyles } from './button-core.styles';
import { ButtonCoreProps } from './button-core.types';

/**
 * ButtonCore - Unified button component with variants, styles, and sizes.
 *
 * A single, flexible button component that supports:
 * - 6 visual variants (primary, secondary, accent-primary, accent-secondary, critical, warning)
 * - 2 styles per variant (filled, outline)
 * - 2 sizes (s: 36px, m: 44px)
 * - Icons, loading state, dropdown support
 *
 * @example
 * ```tsx
 * <ButtonCore variant="primary" style="filled" size="m">
 *   Click me
 * </ButtonCore>
 *
 * <ButtonCore
 *   variant="accent-primary"
 *   style="outline"
 *   size="s"
 *   startIcon={<PlusIcon />}
 *   hasDropdown
 * >
 *   Create
 * </ButtonCore>
 * ```
 */
export const ButtonCore = forwardRef<HTMLButtonElement, ButtonCoreProps>(
  (
    {
      variant = 'primary',
      appearance = 'filled',
      size = 'm',
      startIcon = <IconAdd className='w-4 h-4' />,
      showStartIcon = false,
      endIcon = <IconChevron className='w-4 h-4 -rotate-90' />,
      showEndIcon = false,
      isLoading = false,
      fullWidth = false,
      disabled = false,
      className,
      children,
      type = 'button',
      ...rest
    },
    ref
  ) => {
    const buttonStyles = getButtonCoreStyles(variant, appearance, size, fullWidth);
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={classNames(buttonStyles, className, {
          'cursor-not-allowed': isDisabled,
          'cursor-pointer': !isDisabled,
        })}
        {...rest}>
        <div className={classNames('flex items-center', { 'gap-0.5': size === 's', 'gap-1': size === 'm' })}>
          {/* Start Icon */}
          {showStartIcon && <span className='shrink-0'>{startIcon}</span>}

          {/* Content */}
          <span>{children}</span>

          {/* End Icon or Dropdown */}
          {showEndIcon && <span className='shrink-0'>{endIcon}</span>}

          {/* Loading Indicator */}
          {isLoading && <LoaderSpinner dataTestId='button-core--loader' size={4} color='currentColor' />}
        </div>
      </button>
    );
  }
);

ButtonCore.displayName = 'ButtonCore';
